// app.js
import axios from 'axios';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import './capacitor';
import './panel-cursor-glow';
import './bottom-nav';

// Echo’s Reverb connector expects Pusher on the window
window.Pusher = Pusher;

/**
 * =============================================================================
 * PLATFORM & ENV
 * =============================================================================
 */

const PRODUCTION_WS_HOST = 'ws.business.nexlab.dev';
const isCapacitor = window.Capacitor?.isNativePlatform?.() ?? false;
const isDev = import.meta.env.DEV;
const META = name => document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';

const LOCAL_IP = import.meta.env.VITE_LOCAL_IP || '127.0.0.1';

const API_BASE_URL = isCapacitor
    ? isDev
        ? `http://${LOCAL_IP}`
        : import.meta.env.VITE_API_BASE_URL
    : window.location.origin;

const REVERB_HOST = isDev ? (isCapacitor ? LOCAL_IP : 'localhost') : PRODUCTION_WS_HOST;

const REVERB_PORT = isDev ? Number(import.meta.env.VITE_REVERB_PORT || 8080) : 443;
const HTTP_PORT = isDev ? 80 : 443;

/**
 * =============================================================================
 * AXIOS CONFIGURATION
 * =============================================================================
 */

function configureAxios() {
    window.axios = axios;

    window.axios.defaults.baseURL = API_BASE_URL.replace(/\/$/, '');
    window.axios.defaults.withCredentials = true; // requires proper CORS if cross-origin
    window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

    if (isCapacitor) {
        window.axios.defaults.headers.common['X-Capacitor-App'] = 'true';
        window.axios.defaults.headers.common['X-Capacitor-Platform'] = window.Capacitor.getPlatform();
    }
}

/**
 * =============================================================================
 * ECHO / REVERB CONFIGURATION
 * =============================================================================
 * Keep the client config minimal and correct for Reverb.
 * IMPORTANT: enabledTransports must be ['ws']; TLS makes it 'wss'.
 */

function getBaseEchoConfig() {
    return {
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        enableStats: false,
        withCredentials: true,
        auth: {
            headers: {
                'X-CSRF-TOKEN': META('csrf-token'),
            },
        },
    };
}

function getMobileTransportConfig() {
    const cfg = {
        wsHost: REVERB_HOST,
        wsPort: REVERB_PORT,
        wssPort: REVERB_PORT,
        httpPort: HTTP_PORT,
        httpsPort: 443,
        forceTLS: !isDev,
        encrypted: !isDev,
        // Pusher only accepts 'ws'; TLS upgrades it to 'wss'
        enabledTransports: ['ws'],
    };

    delete cfg.cluster; // ensure wsHost is used
    return cfg;
}

function getWebTransportConfig() {
    const cfg = {
        wsHost: REVERB_HOST,
        wsPort: REVERB_PORT,
        wssPort: REVERB_PORT,
        httpPort: HTTP_PORT,
        httpsPort: 443,
        forceTLS: !isDev, // Always use TLS in production
        encrypted: !isDev, // Match forceTLS for consistency
        enabledTransports: ['ws'],
    };

    delete cfg.cluster;
    return cfg;
}

function createAuthorizer(authEndpoint) {
    const csrf = META('csrf-token');

    return (channel /* Pusher Channel */) => ({
        authorize: (socketId, callback) => {
            if (isDev) {
                console.log('[WebSocket] Authorizing channel', {
                    channel: channel.name,
                    socketId,
                    endpoint: authEndpoint,
                });
            }

            window.axios
                .post(
                    authEndpoint,
                    {
                        socket_id: socketId,
                        channel_name: channel.name,
                    },
                    {
                        headers: {
                            'X-CSRF-TOKEN': csrf,
                            Accept: 'application/json',
                        },
                    }
                )
                .then(response => {
                    if (isDev) console.log('[WebSocket] Auth success');
                    callback(null, response.data);
                })
                .catch(error => {
                    console.error('[WebSocket] Auth error:', error);
                    callback(error, null);
                });
        },
    });
}

function buildEchoConfig() {
    const transport = isCapacitor ? getMobileTransportConfig() : getWebTransportConfig();
    const base = getBaseEchoConfig();

    const authEndpoint = `${API_BASE_URL}/broadcasting/auth`;

    const config = {
        ...base,
        ...transport,

        // Default auth endpoint (used by Echo)
        authEndpoint,

        // Channel authorization for Pusher v8+
        channelAuthorization: {
            endpoint: authEndpoint,
            transport: 'ajax',
            headers: {
                'X-CSRF-TOKEN': META('csrf-token'),
            },
        },

        // Custom authorizer as a fallback using axios (ensures cookies/CSRF)
        authorizer: createAuthorizer(authEndpoint),
    };

    delete config.cluster; // safety
    return config;
}

/**
 * =============================================================================
 * ECHO LIFECYCLE
 * =============================================================================
 */

let _echoRebuildInProgress = false;

function configureEcho() {
    if (_echoRebuildInProgress) return;
    _echoRebuildInProgress = true;

    try {
        // Clean up any previous instance
        if (window.Echo) {
            try {
                window.Echo.leaveAllChannels?.();
            } catch {}
            try {
                window.Echo.connector?.pusher?.disconnect?.();
            } catch {}
        }

        const cfg = buildEchoConfig();

        if (isDev) {
            const { wsHost, wsPort, wssPort, enabledTransports, forceTLS } = cfg;
            console.log('[WebSocket] Rebuilding Echo', {
                wsHost,
                wsPort,
                wssPort,
                enabledTransports,
                forceTLS,
            });
        }

        window.Echo = new Echo(cfg);
        bindEchoConnectionEvents();
    } finally {
        _echoRebuildInProgress = false;
    }
}

function bindEchoConnectionEvents() {
    // Enable Pusher debugging in development
    if (isDev && window.Pusher) {
        window.Pusher.logToConsole = true;
    }

    const pusher = window.Echo?.connector?.pusher;
    if (!pusher) return;

    if (isDev) console.log('[WebSocket] Pusher internal config:', pusher.config);

    const conn = pusher.connection;

    conn.bind('connecting', () => console.log('[WebSocket] Connecting...'));
    conn.bind('connected', () => {
        const cfg = pusher.config || {};
        console.log('[WebSocket] Connected to:', cfg.wsHost || cfg.host);
    });
    conn.bind('failed', () => {
        console.error('[WebSocket] Connection failed');
    });
    conn.bind('error', error => console.error('[WebSocket] Connection error:', error));
    conn.bind('disconnected', () => console.log('[WebSocket] Disconnected'));
}

/**
 * =============================================================================
 * PUSH NOTIFICATIONS
 * =============================================================================
 */

function initializePushNotifications() {
    const init = async () => {
        if (!window.PushNotifications) return;

        try {
            const initialized = await window.PushNotifications.init();
            if (initialized) {
                if (isDev) console.log('[Push Notifications] Initialized');

                // Foreground message handler (Filament friendly)
                await window.PushNotifications.onMessage(payload => {
                    const title = payload?.notification?.title || 'New Notification';
                    const body = payload?.notification?.body || '';

                    if (typeof FilamentNotification !== 'undefined') {
                        new FilamentNotification().title(title).body(body).success().send();
                    } else {
                        console.log('[Push Notification]', { title, body });
                    }
                });
            }
        } catch (error) {
            console.error('[Push Notifications] Initialization failed:', error);
            if (typeof Sentry !== 'undefined' && Sentry.captureException) {
                Sentry.captureException(error, {
                    tags: { component: 'push_notifications', context: 'app_init' },
                    level: 'warning',
                });
            }
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}

/**
 * =============================================================================
 * BOOTSTRAP
 * =============================================================================
 */

configureAxios();
configureEcho();
initializePushNotifications();
